// Type-only: keeps maplibre-gl out of the entry chunk.
import type { FitBoundsOptions } from "maplibre-gl";

/**
 * How far automatic framing keeps markers off the map's edges: past a corner control (a 48px button
 * inset 16px) by the ~22px a marker reaches out from its point.
 */
const FIT_EDGE = 16 + 48 + 24;

export const DEFAULT_FIT_BOUNDS_OPTIONS: FitBoundsOptions = {
  duration: 0,
  // An itinerary with one located entry fits to a zero-area bounds, so without this the camera
  // runs to the map's maxZoom. Keeps automatic framing where it has always been while leaving
  // the user free to pinch deeper.
  maxZoom: 19,
  // The extra 70 at the bottom is the tab bar.
  padding: {
    top: FIT_EDGE,
    bottom: FIT_EDGE + 70,
    left: FIT_EDGE,
    right: FIT_EDGE,
  },
};

/**
 * The closest the recentre control's `activities` mode frames, about a few streets across: one
 * activity on its own would otherwise fit to the default maxZoom and show nothing around it.
 */
export const ACTIVITIES_MAX_ZOOM = 16;

/** What a marker drops to while a search is running and it is not a hit. */
export const SEARCH_MISS_OPACITY = 0.3;

/**
 * How close picking a marker brings the camera, about a neighbourhood across: near enough to see the
 * streets around it, and past `HOTEL_MIN_ZOOM` so the hotels around it show. Only ever zooms in.
 */
export const SELECTION_ZOOM = 15;

/** Where the camera lands on the top hit. Stands need z19: they only leave their cluster past 18. */
export const SEARCH_HIT_ZOOM = 17;
export const SEARCH_HIT_STAND_ZOOM = 19;

/** Hotel prices show from this zoom in, about a town across: further out they pile onto one spot. */
export const HOTEL_MIN_ZOOM = 12;

// Distances between stops. Every leg carries a label once there is room for it; the dotted line draws
// for the active leg alone, since a line per leg is a tangle at the zooms a whole day fits on screen.

/** Distances below this are labelled in metres rather than kilometres. */
export const SHORT_LEG_METRES = 200;

/**
 * The longest gap still counted as a leg. A flight badges as two stops hundreds of kilometres apart, and
 * one such leg would crowd out every walk in the day, its label along with the line it would draw. The
 * two airport markers say "fly" between them.
 */
export const LEG_MAX_KM = 100;

/** From here there is room for a label on each leg. */
export const LEG_LABEL_MIN_ZOOM = 12;

/** What a leg's label drops to while a search is running and neither end is a hit. The active line goes
 * with its pill rather than with these, so it uses `SEARCH_MISS_OPACITY`. */
export const LEG_MISS_OPACITY = 0.15;
